import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaFilter,
  FaEye,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaExclamationTriangle,
  FaChartBar
} from 'react-icons/fa';
import ExpiredClientDetailModal from '../../components/modals/ExpiredClientDetailModal';

const ExpiredClients = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    state: '',
    assignedTo: '',
    expirationReason: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showStats, setShowStats] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expiredClients, setExpiredClients] = useState([]);

  // Initial fetch
  const { data: expiredClientsData, isLoading, error } = useQuery({
    queryKey: ['expiredClients'],
    queryFn: async () => {
      const response = await axios.get(`/api/expired-clients?limit=1000`);
      return response.data;
    },
  });

  // Update local state when data changes
  useEffect(() => {
    if (expiredClientsData?.expiredClients) {
      setExpiredClients(expiredClientsData.expiredClients);
    }
  }, [expiredClientsData]);

  // Manual refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(`/api/expired-clients?limit=1000&_t=${Date.now()}`);
      setExpiredClients(response.data.expiredClients || []);
      toast.success('Data refreshed successfully');
    } catch (err) {
      console.error('Error refreshing data:', err);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Client-side filtering
  const filteredClients = expiredClients.filter(client => {
    const matchesSearch = !filters.search ||
      client.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      client.contact_person?.toLowerCase().includes(filters.search.toLowerCase()) ||
      client.address?.toLowerCase().includes(filters.search.toLowerCase()) ||
      client.phone?.includes(filters.search);

    const matchesCity = !filters.city ||
      client.location?.toLowerCase().includes(filters.city.toLowerCase());

    const matchesState = !filters.state ||
      client.state?.toLowerCase().includes(filters.state.toLowerCase());

    const matchesReason = !filters.expirationReason ||
      client.expirationReason === filters.expirationReason;

    const matchesDateFrom = !filters.dateFrom ||
      new Date(client.expiredAt) >= new Date(filters.dateFrom);

    const matchesDateTo = !filters.dateTo ||
      new Date(client.expiredAt) <= new Date(filters.dateTo);

    return matchesSearch && matchesCity && matchesState && matchesReason && matchesDateFrom && matchesDateTo;
  });

  // Fetch summary statistics
  const { data: statsData } = useQuery({
    queryKey: ['expiredClientsStats'],
    queryFn: async () => {
      const response = await axios.get('/api/expired-clients/stats/summary');
      return response.data;
    }
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: '',
      state: '',
      assignedTo: '',
      expirationReason: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExpirationReasonColor = (reason) => {
    switch (reason) {
      case 'visit_limit_exceeded':
        return 'bg-red-100 text-red-800';
      case 'manual_deletion':
        return 'bg-orange-100 text-orange-800';
      case 'internal_rejection':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExpirationReasonLabel = (reason) => {
    switch (reason) {
      case 'visit_limit_exceeded':
        return 'Visit Limit Exceeded';
      case 'manual_deletion':
        return 'Manually Deleted';
      case 'internal_rejection':
        return 'Internal Rejection';
      default:
        return reason;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Expired Clients</h2>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Expired Clients</h1>
          <p className="text-gray-600 mt-2">
            View and manage gyms that have been moved to expired status
          </p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaChartBar />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRefreshing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </button>
        </div>
      </div>

      {/* Statistics */}
      {showStats && statsData && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Summary Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{statsData.totalExpired}</div>
              <div className="text-sm text-gray-600">Total Expired</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{statsData.expiredThisMonth}</div>
              <div className="text-sm text-gray-600">This Month</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{statsData.expiredThisWeek}</div>
              <div className="text-sm text-gray-600">This Week</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{statsData.averageVisits.toFixed(1)}</div>
              <div className="text-sm text-gray-600">Avg Visits</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-gray-500" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, contact, address..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              placeholder="Filter by city"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input
              type="text"
              placeholder="Filter by state"
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Reason</label>
            <select
              value={filters.expirationReason}
              onChange={(e) => handleFilterChange('expirationReason', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Reasons</option>
              <option value="visit_limit_exceeded">Visit Limit Exceeded</option>
              <option value="manual_deletion">Manual Deletion</option>
              <option value="internal_rejection">Internal Rejection</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Expired Clients ({filteredClients.length})
            </h3>
            {(filters.search || filters.city || filters.state || filters.expirationReason || filters.dateFrom || filters.dateTo) && (
              <div className="text-sm text-blue-600">
                Showing {filteredClients.length} of {expiredClients.length} total
              </div>
            )}
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <FaExclamationTriangle className="text-gray-400 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Expired Clients Found</h3>
            <p className="text-gray-600">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gym Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiration Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{client.name}</div>
                        <div className="text-sm text-gray-500">{client.contact_person}</div>
                        <div className="text-sm text-gray-500">{client.phone}</div>
                        <div className="text-xs text-gray-400 mt-1">
                          Last Stage: {client.stage}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm text-gray-900">{client.location}</div>
                          <div className="text-sm text-gray-500">{client.state}</div>
                          <div className="text-xs text-gray-400">{client.pinCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.assignedUser ? (
                        <div className="flex items-center">
                          <FaUser className="text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {client.assignedUser.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client.assignedUser.role?.name}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getExpirationReasonColor(client.expirationReason)}`}>
                          {getExpirationReasonLabel(client.expirationReason)}
                        </span>
                        <div className="text-sm text-gray-500 mt-1">
                          <FaCalendarAlt className="inline mr-1" />
                          {formatDate(client.expiredAt)}
                        </div>
                        <div className="text-xs text-gray-400">
                          Total Visits: {client.totalVisits}
                        </div>
                        {/* Show rejection reason for internal rejections */}
                        {client.expirationReason === 'internal_rejection' && client.rejectionReason && (
                          <div className="mt-2 p-2 bg-purple-50 rounded-md border border-purple-200">
                            <div className="text-xs font-medium text-purple-700 mb-1">Rejection Reason:</div>
                            <div className="text-xs text-purple-600 break-words max-w-xs">
                              {client.rejectionReason}
                            </div>
                            {client.rejectedBy && (
                              <div className="text-xs text-purple-500 mt-1">
                                By: {client.rejectedBy.name}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                        onClick={() => {
                          setSelectedClient(client);
                          setShowDetailModal(true);
                        }}
                      >
                        <FaEye />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedClient && (
        <ExpiredClientDetailModal
          client={selectedClient}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedClient(null);
          }}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default ExpiredClients;
